import React, { useState, useEffect } from 'react';
import api from '../../api/api';

const KPI_DEFINITIONS = [
  { key: 'openPOs',       label: 'Open Purchase Orders', icon: 'cart',    color: 'dustyBlue',  fallback: 4 },
  { key: 'lowStock',      label: 'Items Below Min Stock', icon: 'alert',  color: 'warmRose',   fallback: 3 },
  { key: 'pendingRecs',   label: 'Overdue Delivery Date', icon: 'layers', color: 'warmAmber', fallback: 2 },
  { key: 'projectPending',  label: 'Pending Projects',   icon: 'folder',  color: 'sageGreen',  fallback: 3 },
  { key: 'prodOrders',    label: 'Pending Production Orders', icon: 'factory', color: 'lavender',   fallback: 1 },
];

const COLORS = {
  dustyBlue:  { bg: 'bg-[#EDF2F7]', text: 'text-[#4A6FA5]', accent: '#4A6FA5' },
  warmRose:   { bg: 'bg-[#FDF0EE]', text: 'text-[#C4574B]', accent: '#C4574B' },
  warmAmber:  { bg: 'bg-[#FEF5E7]', text: 'text-[#B8860B]', accent: '#B8860B' },
  sageGreen:  { bg: 'bg-[#EDF5F0]', text: 'text-[#4A9E6E]', accent: '#4A9E6E' },
  lavender:   { bg: 'bg-[#F1EEF6]', text: 'text-[#7B6FA0]', accent: '#7B6FA0' },
};

/* Column definitions for each KPI detail popup */
const KPI_COLUMNS = {
  openPOs:       [{ key: 'po_number', label: 'PO Number' }, { key: 'vendor_name', label: 'Vendor' }, { key: 'delivery_date', label: 'Delivery Date', fmt: 'date' }, { key: 'line_count', label: 'Lines', align: 'right' }, { key: 'total_amount', label: 'Amount (₹)', align: 'right', fmt: 'money' }],
  lowStock:      [{ key: 'item_no', label: 'Item No.' }, { key: 'description', label: 'Description' }, { key: 'in_stock', label: 'In Stock', align: 'right' }, { key: 'min_order_qty', label: 'Min Qty', align: 'right' }, { key: 'deficit', label: 'Deficit', align: 'right' }],
  pendingRecs: [
    { key: 'project_code', label: 'Project code' },
    { key: 'project_name', label: 'Project name' },
    { key: 'delivery_date', label: 'Overdue delivery date', fmt: 'date' },
  ],
  projectPending: [
    { key: 'project_code', label: 'Project code' },
    { key: 'project_name', label: 'Project name' },
    { key: 'delivery_date', label: 'Delivery date', fmt: 'date' },
  ],
  prodOrders:    [{ key: 'prod_order_no', label: 'Order No.' }, { key: 'item_no', label: 'Item' }, { key: 'description', label: 'Description' }, { key: 'planned_qty', label: 'Qty', align: 'right' }, { key: 'status', label: 'Status' }, { key: 'due_date', label: 'Due Date', fmt: 'date' }],
};

/** MySQL DATE / dateStrings: use calendar day as stored, not UTC shift. */
function fmtCalendarDate(value) {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'string') {
    const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) {
      const y = Number(m[1]);
      const mo = Number(m[2]) - 1;
      const d = Number(m[3]);
      return new Date(y, mo, d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    }
  }
  if (value instanceof Date && !isNaN(value.getTime())) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate()).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  }
  const d = new Date(value);
  return isNaN(d.getTime()) ? String(value) : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function fmtCell(value, fmt) {
  if (value === null || value === undefined || value === '') return '—';
  if (fmt === 'date') {
    return fmtCalendarDate(value);
  }
  if (fmt === 'money') return Number(value).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  return String(value);
}

export default function KPICards({ data: externalData, loading: externalLoading }) {
  const [data, setData] = useState(externalData ?? null);
  const [loading, setLoading] = useState(externalLoading ?? (externalData == null));
  const [popup, setPopup] = useState(null);       // { key, title, rows, loading }

  useEffect(() => {
    if (externalData != null) {
      setData(externalData);
      setLoading(externalLoading ?? false);
      return undefined;
    }
    if (externalLoading === false) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    api.get('/dashboard/kpis')
      .then((kpis) => { if (!cancelled) setData(kpis); })
      .catch(() => {
        if (cancelled) return;
        const fallback = {};
        KPI_DEFINITIONS.forEach((k) => { fallback[k.key] = 0; });
        setData(fallback);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [externalData, externalLoading]);

  const openPopup = (kpiKey) => {
    setPopup({ key: kpiKey, title: 'Loading…', rows: [], loading: true });
    api.get(`/dashboard/kpi-detail/${kpiKey}`)
      .then(d => setPopup({ key: kpiKey, title: d.title || kpiKey, rows: d.rows || [], loading: false }))
      .catch(() => setPopup(p => p ? { ...p, title: 'Error loading data', loading: false } : null));
  };

  if (loading || !data) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
        {KPI_DEFINITIONS.map((kpi) => (
          <div key={kpi.key} className="bg-white/70 rounded-2xl p-4 min-h-[6.5rem] sm:min-h-[7rem] animate-pulse shadow-sm border border-sap-border/30" />
        ))}
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
        {KPI_DEFINITIONS.map((kpi) => {
          const c = COLORS[kpi.color];
          return (
            <div
              key={kpi.key}
              role="button"
              tabIndex={0}
              onClick={() => openPopup(kpi.key)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openPopup(kpi.key);
                }
              }}
              className={`${c.bg} rounded-2xl px-3 sm:px-4 py-4 sm:py-5 flex flex-col items-center justify-center gap-1.5
                min-h-[6.5rem] sm:min-h-[7.25rem] max-w-full
                border border-black/[0.07] shadow-sm
                hover:shadow-md hover:border-black/10 hover:-translate-y-0.5
                active:translate-y-0 active:shadow-sm
                transition-all duration-200 ease-out cursor-pointer select-none
                focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sap-blue`}
            >
              <KPIIcon name={kpi.icon} color={c.accent} />
              <span className={`text-xl sm:text-2xl font-bold tabular-nums tracking-tight ${c.text}`}>
                {data[kpi.key] == null
                  ? '…'
                  : Number.isFinite(Number(data[kpi.key]))
                    ? Number(data[kpi.key]).toLocaleString('en-IN')
                    : String(data[kpi.key])}
              </span>
              <span className="text-[10px] sm:text-2xs text-sap-text/85 text-center font-medium leading-snug max-w-[12rem] px-0.5">
                {kpi.label}
              </span>
            </div>
          );
        })}
      </div>

      {/* KPI Detail Popup */}
      {popup && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={() => setPopup(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-sap-border bg-gray-50 rounded-t-xl">
              <h3 className="text-sm font-bold text-sap-text">{popup.title}</h3>
              <button onClick={() => setPopup(null)} className="text-sap-textMuted hover:text-sap-text text-lg leading-none">✕</button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              {popup.loading ? (
                <p className="text-sm text-slate-400 text-center py-8">Loading…</p>
              ) : popup.rows.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-8">No records found</p>
              ) : (
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-slate-50">
                      {(KPI_COLUMNS[popup.key] || []).map(col => (
                        <th key={col.key} className={`px-3 py-2 font-semibold text-slate-600 ${col.align === 'right' ? 'text-right' : 'text-left'}`}>{col.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {popup.rows.map((row, i) => (
                      <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                        {(KPI_COLUMNS[popup.key] || []).map(col => (
                          <td key={col.key} className={`px-3 py-2 ${col.align === 'right' ? 'text-right' : ''}`}>
                            {fmtCell(row[col.key], col.fmt)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="px-5 py-2.5 border-t border-slate-200 text-right">
              <span className="text-[10px] text-slate-400">{popup.rows.length} record{popup.rows.length !== 1 ? 's' : ''}</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function KPIIcon({ name, color }) {
  const props = { width: 22, height: 22, viewBox: '0 0 24 24', fill: 'none',
    stroke: color, strokeWidth: 1.5, strokeLinecap: 'round', strokeLinejoin: 'round' };

  switch (name) {
    case 'cart':
      return <svg {...props}><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"/></svg>;
    case 'alert':
      return <svg {...props}><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>;
    case 'layers':
      return <svg {...props}><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>;
    case 'folder':
      return <svg {...props}><path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/></svg>;
    case 'box':
      return <svg {...props}><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>;
    case 'factory':
      return <svg {...props}><path d="M2 20h20M4 20V10l5-3v5l5-3v5l5-3v6"/><rect x="6" y="14" width="3" height="3"/><rect x="14" y="14" width="3" height="3"/></svg>;
    default:
      return <svg {...props}><circle cx="12" cy="12" r="10"/></svg>;
  }
}